import { useEffect, useState } from "react";
import { Flex, Text, Grid, Card, Badge, Button, Separator, Dialog, Progress, IconButton, Tooltip, SegmentedControl } from "@radix-ui/themes";
import { CheckCircledIcon, DownloadIcon, CopyIcon } from "@radix-ui/react-icons";
import { useProfileContext, useLocationContext } from "context";
import { useGetGoogleLocationMappings } from "hooks";
import { useGetTripadvisorLocationMappings } from "hooks/integrations/useGetTripadvisorLocationMappings";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";

function LocationCard({ location, googleMapping, tripMapping, googleReviewCount, tripReviewCount, onOpenQrDialog }) {
  const isGoogleConnected = !!googleMapping;
  const isTripConnected = !!tripMapping;
  const totalReviews = (googleReviewCount || 0) + (tripReviewCount || 0);

  const primaryName = location.name;
  const subtitleName = googleMapping?.google_location_name
    ? googleMapping.google_location_name
    : tripMapping?.venue_title || tripMapping?.url_path || tripMapping?.location_name || null;

  return (
    <Card size="2" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Flex direction="column" gap="3" style={{ flex: 1 }}>
        {/* Header */}
        <Flex justify="between" align="start">
          <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
            <Text size="3" weight="bold" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {primaryName}
            </Text>
            {subtitleName && (
              <Text size="2" color="gray" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {subtitleName}
              </Text>
            )}
          </Flex>
          {(isGoogleConnected || isTripConnected) && (
            <Flex direction="column" align="end" style={{ flexShrink: 0 }}>
              <Text size="5" weight="bold" color="blue">{totalReviews}</Text>
              <Text size="1" color="gray">reviews</Text>
            </Flex>
          )}
        </Flex>

        {/* Address */}
        <Flex direction="column" gap="1">
          <Text size="2" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {location.address || "No address"}
          </Text>
          <Text size="2" color="gray">
            {[location.city, location.country].filter(Boolean).join(", ") || ""}
          </Text>
        </Flex>

        <Separator size="4" />

        {/* Platform Connections */}
        <Text size="2" weight="bold">Platform Connections</Text>
        
        <Flex direction="column" gap="2">
          {/* Google */}
          <Flex justify="between" align="center">
            <Flex align="center" gap="2">
              <div style={{ 
                width: 24, 
                height: 24, 
                background: 'var(--gray-3)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '12px', 
                fontWeight: 'bold',
                color: 'var(--gray-11)'
              }}>
                G
              </div>
              <Text size="2">Google</Text>
            </Flex>
            {isGoogleConnected ? (
              <Badge color="green" variant="soft" radius="full">
                <CheckCircledIcon /> Connected
              </Badge>
            ) : (
              <Badge color="gray" variant="soft" radius="full">
                Not connected
              </Badge>
            )}
          </Flex>

          {/* Tripadvisor */}
          <Flex justify="between" align="center">
            <Flex align="center" gap="2">
              <div style={{ 
                width: 24, 
                height: 24, 
                background: 'var(--gray-3)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '10px', 
                fontWeight: 'bold',
                color: 'var(--gray-11)'
              }}>
                TA
              </div>
              <Text size="2">Tripadvisor</Text>
            </Flex>
            {isTripConnected ? (
              <Badge color="green" variant="soft" radius="full">
                <CheckCircledIcon /> Connected
              </Badge>
            ) : (
              <Badge color="gray" variant="soft" radius="full">
                Not connected
              </Badge>
            )}
          </Flex>
        </Flex>

        <div style={{ flex: 1 }} />

        <Separator size="4" />
        
        {/* Footer Action */}
        <Button 
          variant="ghost" 
          style={{ width: '100%', color: 'var(--gray-11)', alignItems: 'center' }}
          onClick={() => onOpenQrDialog(location, googleMapping, tripMapping)}
          disabled={!isGoogleConnected && !isTripConnected}
        >
          Review Links & QR Codes
        </Button>
      </Flex>
    </Card>
  );
}

function QrCodeDialog({ isOpen, onClose, location, googleMapping, tripMapping }) {
  const { appearance } = useTheme();
  const { setToastMessage } = useToast();
  const [colourSelection, setColourSelection] = useState("666666");
  const enabledGoogle = !!googleMapping;
  const enabledTrip = !!tripMapping;
  const [selectedSource, setSelectedSource] = useState(enabledGoogle ? "google" : "tripadvisor");

  useEffect(() => {
    setSelectedSource(enabledGoogle ? "google" : "tripadvisor");
  }, [enabledGoogle, enabledTrip, isOpen]);

  const getQrBackgroundColor = () => {
    return appearance === "dark" ? "1a191c" : "ffffff";
  };

  const getQrUrl = (data, color = "666666", format = "png") => {
    return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data)}&size=200x200&bgcolor=${getQrBackgroundColor()}&color=${color}&format=${format}`;
  };

  const getGoogleReviewLink = () => {
    if (!googleMapping) return "";
    
    // Use the Google Place ID for direct review link
    if (googleMapping.google_place_id) {
      return `https://search.google.com/local/writereview?placeid=${googleMapping.google_place_id}`;
    }
    
    // Fallback to Maps search if no Place ID
    const googleBusinessName = googleMapping.google_location_name || "";
    const googleAddress = googleMapping.google_location_address || "";
    const searchQuery = googleAddress 
      ? `${googleBusinessName} ${googleAddress}` 
      : googleBusinessName;
    
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
  };

  const normalizeTripadvisorPath = (rawPath = "") => {
    let path = rawPath.trim();
    const domainMatch = path.match(/^https?:\/\/([^/]+)(.*)$/i);
    if (domainMatch) {
      path = domainMatch[2] || "";
      path = path.replace(/^\/+/, "");
    }
    return path.replace(/^\/+/, "");
  };

  const getTripadvisorReviewLink = () => {
    if (!tripMapping?.url_path) return "";
    const baseDomainMatch = tripMapping.url_path.match(/^https?:\/\/([^/]+)/i);
    const host = baseDomainMatch ? baseDomainMatch[1] : "www.tripadvisor.com";
    const path = normalizeTripadvisorPath(tripMapping.url_path);

    // Transform Restaurant_Review-...-Reviews-Name.html → UserReviewEdit-...-Name.html
    let tail = path.replace(/^Restaurant_Review-/, "");
    tail = tail.replace(/-Reviews-/, "-");
    const reviewPath = `UserReviewEdit-${tail}`;

    return `https://${host}/${reviewPath}`;
  };

  const reviewLink = selectedSource === "google" ? getGoogleReviewLink() : getTripadvisorReviewLink();
  const qrCodeUrl = reviewLink ? getQrUrl(reviewLink, colourSelection) : "";

  const handleDownload = async () => {
    if (!reviewLink) return;
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${location?.name || "review"}-qr-code.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setToastMessage("QR code downloaded successfully");
    } catch (error) {
      console.error("Error downloading QR code:", error);
      setToastMessage("Failed to download QR code");
    }
  };

  const handleCopyLink = async () => {
    if (!reviewLink) return;
    try {
      await navigator.clipboard.writeText(reviewLink);
      setToastMessage("Link copied to clipboard");
    } catch (error) {
      console.error("Error copying link:", error);
      setToastMessage("Failed to copy link");
    }
  };

  const handleCopyQrCode = async () => {
    if (!qrCodeUrl) return;
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setToastMessage("QR code copied to clipboard");
    } catch (error) {
      console.error("Error copying QR code:", error);
      setToastMessage("Failed to copy QR code");
    }
  };

  const handleColourChange = (event) => {
    let value = event.target.value;
    if (value.startsWith("#")) {
      value = value.slice(1);
    }
    setColourSelection(value);
  };

  if (!location || (!googleMapping && !tripMapping)) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>
          <Flex justify="between" align="center">
            <Text>Review Links & QR Codes</Text>
          </Flex>
        </Dialog.Title>
        <Dialog.Description size="2" mb="2">
          Share these links to collect reviews for {location.name}
        </Dialog.Description>

        <Flex justify="center" pb="2">
          <SegmentedControl.Root value={selectedSource} onValueChange={setSelectedSource} size="2">
            <SegmentedControl.Item value="google" disabled={!enabledGoogle}>Google</SegmentedControl.Item>
            <SegmentedControl.Item value="tripadvisor" disabled={!enabledTrip}>Tripadvisor</SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>

        <Flex direction="column" gap="4">
          {/* Review Link */}
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">{selectedSource === "google" ? "Google Review Link" : "Tripadvisor Review Link"}</Text>
            <Flex 
              align="center" 
              gap="2"
              style={{
                backgroundColor: "var(--gray-a3)",
                padding: "var(--space-2) var(--space-3)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {reviewLink}
              </Text>
              <Tooltip content="Copy link">
                <IconButton size="1" variant="ghost" onClick={handleCopyLink} disabled={!reviewLink}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Flex>
          </Flex>

          <Separator size="4" />

          {/* QR Code */}
          <Flex direction="column" gap="3">
            <Text size="2" weight="medium">QR Code</Text>
            
            <Flex justify="center" p="4" style={{ backgroundColor: "var(--gray-a2)", borderRadius: "var(--radius-3)" }}>
              {qrCodeUrl ? (
                <img 
                  src={qrCodeUrl} 
                  alt="Review QR Code" 
                  style={{ width: 200, height: 200, borderRadius: "var(--radius-2)" }}
                />
              ) : (
                <Text size="2" color="gray">Integrate Google Reviews for this location to generate a QR code</Text>
              )}
            </Flex>

            {/* Color picker */}
            <Flex align="center" gap="2">
              <Text size="2">QR Color:</Text>
              <input
                type="color"
                value={`#${colourSelection}`}
                onChange={handleColourChange}
                style={{ width: 32, height: 32, border: "none", borderRadius: "var(--radius-2)", cursor: "pointer" }}
              />
              <Text size="1" color="gray">#{colourSelection}</Text>
            </Flex>

            {/* QR Code actions */}
            <Flex gap="2">
              <Button variant="soft" onClick={handleDownload} style={{ flex: 1 }} disabled={!reviewLink}>
                <DownloadIcon /> Download
              </Button>
              <Button variant="soft" onClick={handleCopyQrCode} style={{ flex: 1 }} disabled={!qrCodeUrl}>
                <CopyIcon /> Copy
              </Button>
            </Flex>
          </Flex>
        </Flex>

        <Flex gap="3" justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default function ReviewsLocations() {
  const { userProfile } = useProfileContext();
  const { locationsData, isLocationsLoading } = useLocationContext();
  const { data: mappingsData, isLoading: isMappingsLoading } = useGetGoogleLocationMappings(userProfile?.organisation_id);
  const { data: tripMappingsData, isLoading: isTripMappingsLoading } = useGetTripadvisorLocationMappings(userProfile?.organisation_id);
  
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedTripMapping, setSelectedTripMapping] = useState(null);

  // Create maps of location ID to mappings
  const googleMappingsMap = {};
  if (mappingsData?.data?.mappings) {
    mappingsData.data.mappings.forEach((mapping) => {
      googleMappingsMap[mapping.location] = mapping;
    });
  }

  const tripMappingsMap = {};
  if (tripMappingsData?.data?.mappings) {
    tripMappingsData.data.mappings.forEach((mapping) => {
      tripMappingsMap[mapping.location] = mapping;
    });
  }

  const handleOpenQrDialog = (location, mapping, tripMapping) => {
    setSelectedLocation(location);
    setSelectedMapping(mapping);
    setSelectedTripMapping(tripMapping);
    setQrDialogOpen(true);
  };

  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
    setSelectedLocation(null);
    setSelectedMapping(null);
    setSelectedTripMapping(null);
  };

  if (isLocationsLoading || isMappingsLoading || isTripMappingsLoading) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  if (!locationsData || locationsData.length === 0) {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ width: "100%", height: "200px" }}>
        <Text size="3" color="gray">No locations found</Text>
        <Text size="2" color="gray">Add locations in Organisation settings to get started</Text>
      </Flex>
    );
  }

  // Sort locations: connected first, then by name
  const sortedLocations = [...locationsData].sort((a, b) => {
    const aConnected = !!(googleMappingsMap[a.id] || tripMappingsMap[a.id]);
    const bConnected = !!(googleMappingsMap[b.id] || tripMappingsMap[b.id]);
    if (aConnected && !bConnected) return -1;
    if (!aConnected && bConnected) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Flex direction="column" gap="4" style={{ width: "100%", height: "100%", overflowY: "auto", paddingRight: "10px" }}>  
      <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4" width="auto">
        {sortedLocations.map((location) => {
          const googleMapping = googleMappingsMap[location.id];
          const tripMapping = tripMappingsMap[location.id];
          const googleReviewCount = googleMapping?.review_count || 0;
          const tripReviewCount = tripMapping?.review_count || 0;
          
          return (
            <LocationCard 
              key={location.id} 
              location={location} 
              googleMapping={googleMapping}
              tripMapping={tripMapping}
              googleReviewCount={googleReviewCount}
              tripReviewCount={tripReviewCount}
              onOpenQrDialog={handleOpenQrDialog}
            />
          );
        })}
      </Grid>

      <QrCodeDialog 
        isOpen={qrDialogOpen}
        onClose={handleCloseQrDialog}
        location={selectedLocation}
        googleMapping={selectedMapping}
        tripMapping={selectedTripMapping}
      />
    </Flex>
  );
}
